import React, { useMemo, useState } from "react"
import ReactApexChart from "react-apexcharts"
import moment from "moment"
import {
  getSameWeekPreviousYear,
  getWeekTooltipLabel,
  getSameWeekNextYear,
} from "utils/dateUtil"

// ── Sort Order cycling button ────────────────────────────────────────────────
const SORT_STATES = [
  { key: "chronological", icon: "⇅", label: "Sort",       color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" },
  { key: "ascending",     icon: "↑", label: "Low → High", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  { key: "descending",    icon: "↓", label: "High → Low", color: "#0f766e", bg: "#f0fdfa", border: "#99f6e4" },
]

const SortButton = ({ sortOrder, onToggle }) => {
  const current = SORT_STATES.find(s => s.key === sortOrder) || SORT_STATES[0]
  return (
    <button
      onClick={onToggle}
      title={`Sort Order: ${current.label} — click to change`}
      style={{
        display: "flex", alignItems: "center", gap: "8px",
        background: current.bg, border: `1.5px solid ${current.border}`,
        borderRadius: "12px", padding: "9px 16px",
        cursor: "pointer", outline: "none",
        transition: "all 0.18s ease", whiteSpace: "nowrap",
        userSelect: "none", minWidth: "130px",
      }}
    >
      <span style={{ fontSize: "18px", lineHeight: 1, color: current.color, fontWeight: 700, minWidth: "20px", textAlign: "center" }}>
        {current.icon}
      </span>
      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <span style={{ fontSize: "9px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", lineHeight: 1.2 }}>
          Sort Order
        </span>
        <span style={{ fontSize: "12px", fontWeight: 700, color: current.color, lineHeight: 1.4 }}>
          {current.label}
        </span>
      </span>
    </button>
  )
}

// ── Small reusable components ───────────────────────────────────────────────
const StatBox = ({ title, value, color, bg }) => (
  <div
    style={{ flex: 1, background: bg, borderRadius: "10px", padding: "10px 18px", borderLeft: `4px solid ${color}` }}
    data-tooltip={`${title} Value from All the Data`}
  >
    <div style={{ fontSize: "11px", fontWeight: 600 }}>{title}</div>
    <div style={{ fontSize: "18px", fontWeight: 800, color }}>{value}%</div>
  </div>
)

const Legend = ({ color, label, dashed }) => (
  <div className="d-flex align-items-center gap-1">
    <span style={{ width: 16, height: 2, borderTop: dashed ? `2px dashed ${color}` : `2px solid ${color}`, display: "inline-block" }} />
    <small style={{ fontWeight: 600, fontSize: "11px" }}>{label}</small>
  </div>
)

// ── Main OEE Component ───────────────────────────────────────────────────────
const OEEChart = ({
  data,
  previousData,
  sortOrder: sortOrderProp,
  onSortOrderChange,
}) => {
  const [localSortOrder, setLocalSortOrder] = useState("chronological")
  const sortOrder = sortOrderProp !== undefined ? sortOrderProp : localSortOrder
  const setSortOrder = onSortOrderChange ?? setLocalSortOrder

  const handleToggle = () => {
    const next =
      sortOrder === "chronological" ? "ascending"
      : sortOrder === "ascending" ? "descending"
      : "chronological"
    setSortOrder(next)
  }

  // ── Union of all ISO weeks from both years ──────────────────────────────
  const allWeeks = useMemo(() => {
    const weekSet = new Set()
    data?.forEach(i => weekSet.add(moment(i.date).isoWeek()))
    previousData?.forEach(i => weekSet.add(moment(i.date).isoWeek()))
    return Array.from(weekSet).sort((a, b) => a - b)
  }, [data, previousData])

  // ── Current year maps ───────────────────────────────────────────────────
  const efficiencyMap = useMemo(() => {
    const map = {}
    ;(data || []).forEach(i => { map[moment(i.date).isoWeek()] = Number(i.efficiency) || 0 })
    return map
  }, [data])

  const dateMap = useMemo(() => {
    const map = {}
    ;(data || []).forEach(i => { map[moment(i.date).isoWeek()] = moment(i.date).format("DD-MM-YYYY") })
    return map
  }, [data])

  // ── Previous year maps ──────────────────────────────────────────────────
  const prevEfficiencyMap = useMemo(() => {
    const map = {}
    ;(previousData || []).forEach(i => { map[moment(i.date).isoWeek()] = Number(i.efficiency) || 0 })
    return map
  }, [previousData])

  const prevDateMap = useMemo(() => {
    const map = {}
    ;(previousData || []).forEach(i => { map[moment(i.date).isoWeek()] = moment(i.date).format("DD-MM-YYYY") })
    return map
  }, [previousData])

  // ── Apply sort order ────────────────────────────────────────────────────
  const displayWeeks = useMemo(() => {
    if (sortOrder === "ascending") {
      return [...allWeeks].sort((a, b) => {
        const vA = efficiencyMap[a] ?? 0
        const vB = efficiencyMap[b] ?? 0
        return vA !== vB ? vA - vB : a - b
      })
    }
    if (sortOrder === "descending") {
      return [...allWeeks].sort((a, b) => {
        const vA = efficiencyMap[a] ?? 0
        const vB = efficiencyMap[b] ?? 0
        return vA !== vB ? vB - vA : a - b
      })
    }
    return allWeeks
  }, [allWeeks, efficiencyMap, sortOrder])

  // ── Build series from displayWeeks ──────────────────────────────────────
  const categories = displayWeeks.map(w => `${w}`)
  const values     = displayWeeks.map(w => efficiencyMap[w] !== undefined ? efficiencyMap[w] : null)
  const prevValues = displayWeeks.map(w => prevEfficiencyMap[w] !== undefined ? prevEfficiencyMap[w] : null)

  // ── Year labels ─────────────────────────────────────────────────────────
  const currentYear  = data?.length ? moment(data[0].date).year() : moment().year()
  const previousYear = previousData?.length ? moment(previousData[0].date).year() : currentYear - 1

  // ── Stats ───────────────────────────────────────────────────────────────
  const validValues   = values.filter(v => v !== null)
  const avgEfficiency = validValues.length ? (validValues.reduce((a, b) => a + b, 0) / validValues.length).toFixed(1) : 0
  const maxVal        = validValues.length ? Math.max(...validValues) : 0
  const minVal        = validValues.length ? Math.min(...validValues) : 0

  // ── Chart options ───────────────────────────────────────────────────────
  const chartOptions = {
    chart: {
      type: "bar", height: 340, toolbar: { show: false },
      background: "transparent", foreColor: "#1a1a2e",
      animations: { enabled: true, speed: 400 },
    },
    plotOptions: { bar: { borderRadius: 6, columnWidth: "55%" } },
    colors: ["#6366f1", "#22c55e", "#f59e0b"],
    dataLabels: { enabled: false },
    stroke: {
      show: true, width: [0, 2.5, 2.5], curve: "smooth",
      dashArray: [0, 0, 6], colors: ["transparent", "#22c55e", "#f59e0b"],
    },
    xaxis: {
      categories,
      overwriteCategories: categories,
      axisBorder: { show: false },
      labels: { rotate: -45, style: { colors: "#1a1a2e", fontSize: "12px", fontWeight: 600 } },
    },
    yaxis: {
      title: { text: "OEE (%)", style: { color: "#1a1a2e", fontSize: "12px", fontWeight: 700 } },
      min: 0, max: 120,
      labels: { formatter: val => `${val}%`, style: { colors: "#1a1a2e", fontSize: "12px", fontWeight: 600 } },
    },
    grid: {
      borderColor: "#e8e8f0", strokeDashArray: 4,
      xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } },
    },
    tooltip: {
      shared: true, theme: "light", intersect: false,
      x: {
        formatter: (val, { dataPointIndex }) => {
          const weekNum             = displayWeeks[dataPointIndex]
          const currentDate         = dateMap[weekNum]
          const prevDate            = prevDateMap[weekNum]
          const resolvedCurrentDate = currentDate ?? (prevDate ? getSameWeekNextYear(prevDate) : null)
          const resolvedPrevDate    = prevDate ?? (currentDate ? getSameWeekPreviousYear(currentDate) : null)
          const currentLabel        = getWeekTooltipLabel(weekNum - 1, resolvedCurrentDate)
          const prevLabel           = getWeekTooltipLabel(weekNum - 1, resolvedPrevDate)
          return `Current Year : ${currentLabel || "N/A"}<br/> Previous Year : ${prevLabel || "N/A"}`
        },
      },
      y: {
        formatter: (val, { seriesIndex }) => {
          if (seriesIndex === 0) return undefined
          return val !== null && val !== undefined ? `${val}%` : "N/A"
        },
      },
    },
    legend: { show: false },
  }

  const chartSeries = [
    { name: "OEE Efficiency",               type: "bar",  data: values },
    { name: `Current Year (${currentYear})`,  type: "line", data: values },
    { name: `Previous Year (${previousYear})`, type: "line", data: prevValues },
  ]

  return (
    <div>
      {/* ── Header ── */}
      <div className="d-flex justify-content-between align-items-start mb-2 px-2 flex-wrap gap-2">
        <h5 className="mb-2 fw-bold" style={{ color: "#1a1a2e", fontSize: "16px" }}>
          OEE — Overall Equipment Effectiveness
        </h5>

        <div className="d-flex gap-3 align-items-center flex-wrap">
          {/* Sort Order cycling button */}
          <SortButton sortOrder={sortOrder} onToggle={handleToggle} />

          {/* Stats */}
          <div className="d-flex gap-4 px-2">
            <StatBox title="PEAK"    value={maxVal}        color="#22c55e" bg="#f0fdf4" />
            <StatBox title="AVERAGE" value={avgEfficiency} color="#6366f1" bg="#eef0fb" />
            <StatBox title="LOWEST"  value={minVal}        color="#ef4444" bg="#fef2f2" />
          </div>
        </div>
      </div>

      {/* ── Chart ── */}
      <ReactApexChart options={chartOptions} series={chartSeries} type="bar" height={320} width="100%" />

      {/* ── Legend ── */}
      <div className="d-flex justify-content-center gap-4 mt-1 mb-2">
        <Legend color="#22c55e" label={`Current Year (${currentYear})`} />
        <Legend color="#f59e0b" label={`Previous Year (${previousYear})`} dashed />
      </div>
    </div>
  )
}

export default OEEChart
